/* hb-combo-buy.js — makes the combo boxes on a product page clickable and buyable.
 *
 * Runs alongside hb-combo.js and never modifies it: it watches #pdpcList and
 * enhances whatever that script renders, the same MutationObserver pattern
 * hb-flags.js uses. Every node it touches gets a data- flag and every query
 * uses :not([flag]), so the observer seeing its own work is a no-op.
 *
 * Adds:
 *   1. a real <a> over each card's art and name, so any part of a combo box
 *      opens that card (its product page if we stock it, a search if we don't)
 *   2. an "Add N of M to cart" button per combo, which live-verifies each
 *      product's variants at click time and adds the pieces we hold.
 */
(function () {
  'use strict';

  var Q = String.fromCharCode(63);
  var LIST = '#pdpcList';

  function txt(el) { return el ? (el.textContent || '').trim() : ''; }

  /* ---------- 1. every card in a combo becomes a link ---------- */

  function linkFigures(scope) {
    var figs = scope.querySelectorAll('.cmb__fig:not([data-hb-linked])');
    Array.prototype.forEach.call(figs, function (fig) {
      var art = fig.querySelector('.cmb__art');
      var chip = fig.querySelector('.cmb__stock');
      if (!art) { fig.setAttribute('data-hb-linked', 'skip'); return; }

      /* the card you are already looking at, or one already in your deck */
      if (chip && chip.classList.contains('cmb__stock--this')) {
        fig.setAttribute('data-hb-linked', 'this'); return;
      }
      /* stock not resolved yet - leave it for the next observer pass */
      if (!chip || chip.classList.contains('cmb__stock--wait')) return;

      var a = chip.querySelector('a');
      if (!a) { fig.setAttribute('data-hb-linked', 'skip'); return; }
      var href = a.getAttribute('href');
      if (!href) { fig.setAttribute('data-hb-linked', 'skip'); return; }

      fig.setAttribute('data-hb-linked', '1');

      var name = txt(fig.querySelector('.cmb__cname')) || 'this card';
      var over = document.createElement('a');
      over.className = 'cmb__figlink';
      over.href = href;
      over.setAttribute('aria-label', name);
      art.appendChild(over);

      var nm = fig.querySelector('.cmb__cname');
      if (nm && !nm.querySelector('a')) {
        var na = document.createElement('a');
        na.className = 'cmb__cnamelink';
        na.href = href;
        na.textContent = nm.textContent;
        nm.textContent = '';
        nm.appendChild(na);
      }
    });
  }

  /* ---------- 2. the buy button ---------- */

  function thisProductUrl() {
    var p = window.location.pathname;
    return p.indexOf('/products/') === 0 || p.indexOf('/products/') > -1 ? p : null;
  }

  /* Read one combo box and work out what of it we can actually sell. */
  function readCombo(art) {
    var have = [], missing = [], pending = false;
    var figs = art.querySelectorAll('.cmb__fig');
    Array.prototype.forEach.call(figs, function (fig) {
      if (fig.classList.contains('cmb__fig--txt')) return;      /* "a creature" style requirement */
      var chip = fig.querySelector('.cmb__stock');
      if (!chip) return;
      var name = txt(fig.querySelector('.cmb__cname'));
      if (chip.classList.contains('cmb__stock--wait')) { pending = true; return; }
      if (chip.classList.contains('cmb__stock--this')) {
        var u = thisProductUrl();
        if (u) have.push({ name: name, url: u });
        return;
      }
      var a = chip.querySelector('a');
      var href = a ? a.getAttribute('href') : '';
      if (chip.classList.contains('cmb__stock--yes') && href.indexOf('/products/') > -1) {
        have.push({ name: name, url: href.split(Q)[0] });
      } else {
        missing.push({ name: name, href: href });
      }
    });
    return { have: have, missing: missing, pending: pending };
  }

  function addFoot(art) {
    var info = readCombo(art);
    if (info.pending) return;                       /* wait for stock to resolve */
    art.setAttribute('data-hb-buy', '1');

    var body = art.querySelector('.cmb__body') || art;
    var total = info.have.length + info.missing.length;
    if (!total) return;

    var foot = document.createElement('div');
    foot.className = 'cmb__foot';

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'cmb__buy';
    if (!info.have.length) {
      btn.disabled = true;
      btn.textContent = 'None of these in stock';
    } else if (!info.missing.length) {
      btn.textContent = 'Add all ' + info.have.length + ' to cart';
    } else {
      btn.textContent = 'Add ' + info.have.length + ' of ' + total + ' to cart';
    }
    btn.__hb = info;
    foot.appendChild(btn);

    if (info.missing.length) {
      var note = document.createElement('span');
      note.className = 'cmb__need';
      note.appendChild(document.createTextNode('Not on our shelf: '));
      info.missing.forEach(function (m, i) {
        if (i) note.appendChild(document.createTextNode(', '));
        var a = document.createElement('a');
        a.href = m.href || ('/search' + Q + 'q=' + encodeURIComponent(m.name));
        a.textContent = m.name;
        note.appendChild(a);
      });
      foot.appendChild(note);
    }
    body.appendChild(foot);
  }

  /* ---------- adding to the cart ---------- */

  /* Live-verify at click time rather than trusting the snapshot: ask the
     product itself which variant is available right now. */
  function firstAvailableVariant(url) {
    return fetch(url + '.js', { headers: { accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        if (!p || !p.variants) return null;
        for (var i = 0; i < p.variants.length; i++) {
          if (p.variants[i].available) return p.variants[i].id;
        }
        return null;
      })
      .catch(function () { return null; });
  }

  function cartVariantIds() {
    return fetch('/cart.js', { headers: { accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : { items: [] }; })
      .then(function (c) { return (c.items || []).map(function (i) { return i.id; }); })
      .catch(function () { return []; });
  }

  function bumpBubble() {
    fetch('/cart.js', { headers: { accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) {
        var b = document.querySelector('.cart-count-bubble');
        if (!b) return;
        var s = b.querySelector('span[aria-hidden]') || b.querySelector('span');
        if (s) s.textContent = c.item_count;
        b.hidden = false;
      })
      .catch(function () {});
  }

  function buy(btn) {
    var info = btn.__hb;
    if (!info || !info.have.length || btn.dataset.busy) return;
    btn.dataset.busy = '1';
    var label = btn.textContent;
    btn.textContent = 'Adding…';

    Promise.all(info.have.map(function (h) {
      return firstAvailableVariant(h.url).then(function (id) {
        return id ? { id: id, name: h.name } : null;
      });
    })).then(function (found) {
      var live = found.filter(Boolean);
      if (!live.length) throw new Error('sold out');
      return cartVariantIds().then(function (already) {
        /* Commander is singleton - if a piece is already in the cart, leave
           its quantity alone rather than pushing it to 2. */
        var items = live
          .filter(function (v) { return already.indexOf(v.id) === -1; })
          .map(function (v) { return { id: v.id, quantity: 1 }; });
        if (!items.length) {
          btn.textContent = 'Already in your cart';
          btn.classList.add('is-done');
          return null;
        }
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'content-type': 'application/json', accept: 'application/json' },
          body: JSON.stringify({ items: items })
        }).then(function (r) {
          if (r.ok) return r.json();
          /* /cart/add.js rejects the whole request if any one line exceeds
             stock, and does not say which. Fall back to adding them singly. */
          return items.reduce(function (chain, it) {
            return chain.then(function (n) {
              return fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'content-type': 'application/json', accept: 'application/json' },
                body: JSON.stringify({ items: [it] })
              }).then(function (rr) { return n + (rr.ok ? 1 : 0); });
            });
          }, Promise.resolve(0)).then(function (n) {
            if (!n) throw new Error('none added');
            return { partial: n };
          });
        }).then(function (res) {
          btn.textContent = res && res.partial
            ? 'Added ' + res.partial + ' ✓'
            : 'Added to cart ✓';
          btn.classList.add('is-done');
          bumpBubble();
          return res;
        });
      });
    }).catch(function () {
      btn.textContent = 'Just sold out — try again';
    }).then(function () {
      delete btn.dataset.busy;
      setTimeout(function () {
        if (!btn.classList.contains('is-done')) btn.textContent = label;
      }, 3200);
    });
  }

  document.addEventListener('click', function (e) {
    var b = e.target.closest && e.target.closest('.cmb__buy');
    if (b) { e.preventDefault(); buy(b); }
  });

  /* ---------- observer ---------- */

  var timer = null;
  function sweep() {
    var list = document.querySelector(LIST);
    if (!list) return;
    linkFigures(list);
    var arts = list.querySelectorAll('.cmb__combo:not([data-hb-buy])');
    Array.prototype.forEach.call(arts, addFoot);
  }
  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(sweep, 150);
  }

  function start() {
    var list = document.querySelector(LIST);
    if (!list) return;
    sweep();
    new MutationObserver(schedule).observe(list, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else { start(); }
})();
